import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import RefreshIcon from '@mui/icons-material/Refresh';
import { Autocomplete, Box, Button, Chip, IconButton, TextField, Tooltip, Typography } from '@mui/material';
import type { ReactNode } from 'react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNotify } from 'react-admin';
import { AGENT_STATUS_COLORS, AGENT_STATUS_DISPLAY } from '../../../constants/agentStatus';
import {
  AgentAvailabilityStatus,
  AgentDirectoryEntry,
  DirectoryTrackingNumber,
  SearchAgentsParams,
} from '../../../interfaces/calls';
import { Pagination } from '../../../interfaces/common';
import voiceService from '../../../services/appointments/calls';
import agentsService from '../../../services/appointments/agents';
import { CustomTable } from '../../common/CustomTable';
import AgentsFilter from '../filters/AgentsFilter';
import './agents-center.css';
import { OverrideAgentStatusModal } from './OverrideAgentStatusModal';
import { AgentEditModal } from './AgentEditModal';

type Props = {
  breadcrumbs: ReactNode;
};

const INITIAL_PAGINATION_STATE = {
  currentPage: 1,
  perPage: 10,
  totalCount: 0,
  totalPages: 0,
};

const DEFAULT_PAGE_SIZE = 10;

export const AgentsCenter = ({ breadcrumbs }: Props) => {
  const [agents, setAgents] = useState<AgentDirectoryEntry[]>([]);
  const [page, setPage] = useState<number>(1);
  const [filters, setFilters] = useState<SearchAgentsParams>({});
  const [pagination, setPagination] = useState<Pagination>(INITIAL_PAGINATION_STATE);
  const [loading, setLoading] = useState<boolean>(false);
  const [overrideModalOpen, setOverrideModalOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [selectedAgentForStatus, setSelectedAgentForStatus] = useState<{
    id: number;
    name: string;
    currentStatus: AgentAvailabilityStatus;
  } | null>(null);
  const [selectedAgentIdForEdit, setSelectedAgentIdForEdit] = useState<number | undefined>(undefined);
  
  const isMountedRef = useRef<boolean>(true);

  const notify = useNotify();

  const fetchAgents = useCallback(async () => {
    try {
      setLoading(true);

      const cleanedFilters = Object.entries(filters).reduce((acc, [key, value]) => {
        if (value !== '' && value !== undefined && value !== null) {
          acc[key] = value;
        }
        return acc;
      }, {} as any);

      const response = await voiceService.searchAgents({
        page: page,
        pageSize: pagination.perPage,
        ...cleanedFilters,
      });

      if (!isMountedRef.current) {
        return;
      }

      if (!response.data) {
        notify('Failed to fetch agents', 'error');
        return;
      }

      setAgents(response.data);

      if (response.pagination) {
        setPagination(response.pagination);
      } else {
        setPagination({
          ...INITIAL_PAGINATION_STATE,
          totalCount: response.data.length,
          totalPages: Math.ceil(response.data.length / DEFAULT_PAGE_SIZE),
        });
      }
    } catch (error) {
      if (!isMountedRef.current) {
        return;
      }
      notify('Failed to fetch agents', 'error');
    }

    if (isMountedRef.current) {
      setLoading(false);
    }
  }, [filters, pagination.perPage, notify, page]);

  const handleFilterChanges = (filter?: { [filter: string]: number | string | undefined }) => {
    const updatedFilters = {
      ...filters,
      ...filter,
    };

    setFilters(updatedFilters);
    setPagination((prev) => ({ ...prev, currentPage: 1 }));
  };

  const handleRefresh = useCallback(() => {
    setPage(1);
    fetchAgents();
  }, [fetchAgents]);

  const handleOverrideStatus = (agent: AgentDirectoryEntry) => {
    setSelectedAgentForStatus({
      id: agent.id,
      name: agent.name,
      currentStatus: agent.status,
    });
    setOverrideModalOpen(true);
  };

  const handleEditAgent = (agentId: number) => {
    setSelectedAgentIdForEdit(agentId);
    setEditModalOpen(true);
  };

  const handleCreateAgent = () => {
    setSelectedAgentIdForEdit(undefined);
    setEditModalOpen(true);
  };

  const handleSuccess = () => {
    fetchAgents();
  };

  const handleDeleteAgent = async (agentId: number, agentName: string) => {
    if (!window.confirm(`Are you sure you want to delete agent "${agentName}"? This action cannot be undone.`)) {
        return;
    }

    try {
        setLoading(true);
        await agentsService.deleteAgent(agentId);
        notify('Agent deleted successfully', { type: 'success' });
        fetchAgents();
    } catch (error) {
        notify('Failed to delete agent', { type: 'error' });
    } finally {
        setLoading(false);
    }
  };

  useEffect(() => {
    isMountedRef.current = true;
    fetchAgents();

    return () => {
      isMountedRef.current = false;
    };
  }, [filters, fetchAgents, page]);

  const rows = useMemo(
    () =>
      agents.map((agent) => ({
        agent: (
          <Box flexDirection='column' display='flex'>
            <Typography variant='body2' fontWeight="bold" sx={{ cursor: 'pointer', textDecoration: 'underline' }} onClick={() => handleEditAgent(agent.id)}>{agent.name}</Typography>
            <Typography variant='caption' color='textSecondary'>
              ID: {agent.coreProviderId}
            </Typography>
          </Box>
        ),
        phones: (() => {
          const allNumbers: { number: string; usages: Set<string>; label?: string | null }[] = [];

          const addOrUpdateNumber = (num: DirectoryTrackingNumber, usage: string) => {
            let entry = allNumbers.find((n) => n.number === num.number);
            if (!entry) {
              entry = { number: num.number, usages: new Set(), label: num.label };
              allNumbers.push(entry);
            }
            entry.usages.add(usage);
          };

          agent.trackingNumbers?.incoming?.forEach((num) => addOrUpdateNumber(num, 'In'));
          agent.trackingNumbers?.outgoing?.forEach((num) => addOrUpdateNumber(num, 'Out'));
          agent.trackingNumbers?.sms?.forEach((num) => addOrUpdateNumber(num, 'SMS'));

          if (allNumbers.length === 0) {
            return <Typography variant='body2' color='textSecondary'>-</Typography>;
          }

          return (
            <Box display='flex' flexDirection='column' gap={0.5}>
              {allNumbers.map((numEntry, index) => (
                <Box display='flex' alignItems='center' gap={0.5} key={`${agent.id}-phone-${index}`}>
                  <Typography variant='body2' sx={{ fontSize: '12px' }}>
                    {numEntry.number}
                  </Typography>
                  <Chip
                    size='small'
                    label={`(${Array.from(numEntry.usages).join(', ')})`}
                    sx={{ fontSize: '10px', height: '20px', backgroundColor: '#e0e0e0' }}
                  />
                  {numEntry.label && (
                    <Chip
                      size='small'
                      label={numEntry.label}
                      variant='outlined'
                      sx={{ fontSize: '10px', height: '20px' }}
                    />
                  )}
                </Box>
              ))}
            </Box>
          );
        })(),
        status: (
          <Box display='flex' alignItems='center' gap={1}>
            <Chip
              size='small'
              label={AGENT_STATUS_DISPLAY[agent.status]}
              sx={{
                fontSize: '12px',
                backgroundColor: AGENT_STATUS_COLORS[agent.status],
                color: 'white',
              }}
            />
            <Tooltip title='Override Status'>
              <IconButton
                size='small'
                onClick={() => handleOverrideStatus(agent)}
                sx={{ padding: '4px' }}>
                <EditIcon fontSize='small' />
              </IconButton>
            </Tooltip>
          </Box>
        ),
        teams: (
            <Box display='flex' gap={0.5} flexWrap='wrap'>
                {agent.agentTeams && agent.agentTeams.length > 0 ? (
                    agent.agentTeams.map((agentTeam) => (
                        <Chip
                            key={agentTeam.team.id}
                            size='small'
                            label={agentTeam.team.name}
                            variant='outlined'
                            sx={{ fontSize: '11px' }}
                        />
                    ))
                ) : (
                    <Typography variant='body2' color='textSecondary'>
                        -
                    </Typography>
                )}
            </Box>
        ),
        queues: (
          <Box display='flex' gap={0.5} flexWrap='wrap'>
            {agent.queues && agent.queues.length > 0 ? (
              agent.queues.map((queue) => (
                <Chip
                  key={queue.id}
                  size='small'
                  label={queue.name}
                  variant='outlined'
                  sx={{ fontSize: '11px' }}
                />
              ))
            ) : (
              <Typography variant='body2' color='textSecondary'>
                -
              </Typography>
            )}
          </Box>
        ),
        actions: (
            <Tooltip title='Delete Agent'>
                <IconButton size='small' onClick={() => handleDeleteAgent(agent.id, agent.name)} color="error">
                    <DeleteIcon fontSize='small' />
                </IconButton>
            </Tooltip>
        ),
      })),
    [agents],
  );

  return (
    <Box className='tab-section agents-center-section' marginTop={0}>
      <Box className='agents-center-header'>
        <Box display='flex' justifyContent='space-between' alignContent='center' marginBottom='8px'>
          {breadcrumbs}
          <Button
            variant='contained'
            color='primary'
            startIcon={<AddIcon />}
            onClick={handleCreateAgent}
          >
            Create Agent
          </Button>
        </Box>

        <Box display='flex' alignItems='flex-start' gap={1} mt={2} mb={3} flexWrap='wrap'>
          {/* Agent Filter */}
          <AgentsFilter
            value={filters.coreProviderId}
            filterType='coreProviderId'
            handleFilterChanges={handleFilterChanges}
          />

          {/* Status Filter */}
          <Box display='flex' flexDirection='column' alignItems='flex-start' marginBottom={2}>
            <Typography sx={{ '&.MuiTypography-root': { fontSize: '13px' } }}>Status</Typography>
            <Autocomplete
              size='small'
              options={Object.values(AgentAvailabilityStatus).filter(
                (s) => s !== AgentAvailabilityStatus.DISABLED,
              )}
              getOptionLabel={(option) => AGENT_STATUS_DISPLAY[option]}
              onChange={(_, value) => handleFilterChanges({ status: value || undefined })}
              renderInput={(params) => (
                <TextField {...params} placeholder='All status' sx={{ width: '150px' }} />
              )}
              sx={{ width: '150px' }}
            />
          </Box>

          {/* Refresh */}
          <Box
            display='flex'
            flexDirection='column'
            alignItems='center'
            justifyContent='center'
            className='messages-center-select'>
            <Typography>&nbsp;</Typography>
            <Box display='flex' flexDirection='column' alignItems='center' gap={2}>
              <RefreshIcon onClick={handleRefresh} sx={{ cursor: 'pointer' }} />
            </Box>
          </Box>
        </Box>
      </Box>
      <CustomTable
        columns={[
          { key: 'agent', label: 'Agent' },
          { key: 'phones', label: 'Phone Numbers', width: '250px', minWidth: '250px' },
          { key: 'status', label: 'Status', width: '130px', minWidth: '130px' },
          { key: 'teams', label: 'Teams', width: '150px', minWidth: '150px' },
          { key: 'queues', label: 'Queues', width: '200px', minWidth: '200px' },
          { key: 'actions', label: '', width: '50px' },
        ]}
        defaultText='No agents found'
        key='agents-center-table'
        loading={loading}
        rows={rows}
        pagination={{
          rowsPerPageOptions: [DEFAULT_PAGE_SIZE],
          rowPerPage: pagination.perPage || 1,
          page: pagination.currentPage || 1,
          totalCount: pagination.totalCount || 0,
          onPageChange: (page: number) => setPage(page),
        }}
      />
      <OverrideAgentStatusModal
        open={overrideModalOpen}
        onClose={() => setOverrideModalOpen(false)}
        agent={selectedAgentForStatus}
        onSuccess={handleSuccess}
      />
      <AgentEditModal
        open={editModalOpen}
        onClose={() => setEditModalOpen(false)}
        agentId={selectedAgentIdForEdit}
        onSuccess={handleSuccess}
      />
    </Box>
  );
};
